import { CheckCircle, Circle, WarningOctagon, MagnifyingGlass, Wrench } from "@phosphor-icons/react";

export default function CccBlueprint({ doc, onToggle }) {
  const p = doc.payload || {};
  const whys = (p.whys || []).filter((w) => w && w.trim());
  const cms = p.countermeasures || [];
  const progress = doc.progress || { done: 0, total: 0, pct: 0 };

  return (
    <div className="space-y-4" data-testid="ccc-blueprint">
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-500">Countermeasure Progress</span>
          <span className="font-mono text-sm font-semibold text-slate-700" data-testid="progress-percent">{progress.done}/{progress.total} · {progress.pct}%</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-sm overflow-hidden"><div className={`h-full ${progress.pct === 100 ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${progress.pct}%` }} /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div data-testid="ccc-concern" className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 bg-red-50"><WarningOctagon size={18} weight="duotone" className="text-red-600" /><h3 className="text-sm font-semibold uppercase tracking-wide text-red-700">Concern</h3></div>
          <div className="p-4 space-y-2 text-sm text-slate-800">
            <p className="whitespace-pre-wrap">{p.concern || <span className="text-slate-300">—</span>}</p>
            <p className="text-xs text-slate-500"><span className="font-semibold">Location:</span> {p.location || "—"}</p>
            <p className="text-xs text-slate-500"><span className="font-semibold">Date / Shift:</span> {p.dateShift || "—"}</p>
            <p className="text-xs text-slate-500"><span className="font-semibold">Exposure:</span> {p.exposure || "—"}</p>
          </div>
        </div>

        <div data-testid="ccc-cause" className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 bg-amber-50"><MagnifyingGlass size={18} weight="duotone" className="text-amber-700" /><h3 className="text-sm font-semibold uppercase tracking-wide text-amber-700">Cause (5-Whys)</h3></div>
          <div className="p-4">
            {whys.length === 0 ? <p className="text-sm text-slate-300">—</p> : (
              <ol className="space-y-2">
                {whys.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-800"><span className="font-mono text-[10px] text-amber-700 w-10 shrink-0">WHY {i + 1}</span><span>{w}</span></li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <div data-testid="ccc-countermeasure" className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 bg-blue-50"><Wrench size={18} weight="duotone" className="text-blue-700" /><h3 className="text-sm font-semibold uppercase tracking-wide text-blue-700">Countermeasures</h3></div>
          <div className="divide-y divide-slate-100">
            {cms.length === 0 && <p className="px-4 py-3 text-sm text-slate-400">No countermeasures.</p>}
            {cms.map((c) => {
              const Row = onToggle ? "button" : "div";
              return (
                <Row key={c.id} data-testid={`ccc-task-${c.id}`} {...(onToggle ? { onClick: () => onToggle(c.id, c.status) } : {})} className={`w-full flex items-start gap-2.5 px-4 py-3 text-left ${onToggle ? "hover:bg-slate-50" : ""}`}>
                  {c.status ? <CheckCircle size={20} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /> : <Circle size={20} className="text-slate-300 shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <p className={`text-sm ${c.status ? "text-slate-400 line-through" : "text-slate-800"}`}>{c.name || "Untitled"}</p>
                    <p className="text-xs text-slate-400">{c.owner || "Unassigned"}{c.deadline ? ` · ${c.deadline}` : ""}</p>
                  </div>
                </Row>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
